import type { ReactNode } from "react";
import { createPortal } from "react-dom";
import type { ReferralStage } from "../../../shared/types";
import { Icon, type IconName } from "../../components/ui/Icon";
import { Toast } from "../../components/ui/Toast";

/** Two-letter monogram from a name or a "J. Marshall" style label. */
export function initials(label: string): string {
  const parts = label.trim().split(/\s+/).filter(Boolean);
  const a = parts[0]?.charAt(0) ?? "";
  const b = parts.length > 1 ? parts[parts.length - 1].charAt(0) : "";
  return (a + b).toUpperCase() || "?";
}

/** A person's round monogram: partners have no photos, so their initials stand in. */
export function Monogram({ name, small = false }: { name: string; small?: boolean }) {
  return (
    <span className={small ? "pa-av sm" : "pa-av"} aria-hidden="true">
      {initials(name)}
    </span>
  );
}

/** "Oct 7" from a 'YYYY-MM-DD' or 'YYYY-MM-DD HH:MM:SS' stamp; empty when unparseable. */
export function dayLabel(stamp: string | null, withYear = false): string {
  if (!stamp) return "";
  const d = new Date(stamp.trim().slice(0, 10) + "T00:00:00Z");
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    ...(withYear ? { year: "numeric" } : {}),
    timeZone: "UTC",
  });
}

/** A partner's referral link: the full address to copy, and how it reads without its https://. */
export function linkParts(linkBase: string | null, slug: string): { shown: string; full: string } {
  const full = linkBase ? `${linkBase}${slug}` : `/go?p=${slug}`;
  return { shown: full.replace(/^https?:\/\//, ""), full };
}

/** Where a referred person has got to, in words. */
export const STAGE_LABEL: Record<ReferralStage, string> = {
  click: "Clicked",
  signup: "Signed up",
  trial: "Trial",
  customer: "Customer",
  onhold: "On hold",
  churned: "Churned",
};

export const plural = (n: number, one: string, many = `${one}s`): string => `${n.toLocaleString("en-US")} ${n === 1 ? one : many}`;

/** What a copy says out loud. */
export const copiedNote = (label: string, ok: boolean): string => (ok ? `${label} copied` : "Couldn't copy. Your browser blocked it.");

/** Puts text on the clipboard; false when the browser refuses. */
export async function copyText(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    return false;
  }
}

/**
 * A row that copies what it shows with one tap: the label small above, the value below, the copy mark on
 * the right. `onCopied` says it out loud.
 */
export function CopyRow({
  label,
  value,
  shown,
  icon,
  big = false,
  onCopied,
}: {
  label: string;
  value: string;
  /** What the row shows, when it differs from what it copies (a link without its https://). */
  shown?: string;
  icon?: IconName;
  /** The label as the row's title (a link row), rather than a small line over the value. */
  big?: boolean;
  onCopied: (label: string, ok: boolean) => void;
}) {
  return (
    <button
      type="button"
      className={big ? "pn-row pa-cp big" : "pn-row pa-cp"}
      onClick={async () => onCopied(label, await copyText(value))}
      aria-label={`Copy ${label}`}
    >
      {icon ? (
        <span className="pn-ico">
          <Icon name={icon} />
        </span>
      ) : null}
      <span className="m">
        {big ? <span className="t">{label}</span> : <span className="s">{label}</span>}
        <span className={big ? "s" : "t pa-nums"}>{shown ?? value}</span>
      </span>
      <span className="pa-cpi">
        <Icon name="copy" />
      </span>
    </button>
  );
}

/** The small line of figures under a total: each a number over its name. */
export function Figures({ items }: { items: [ReactNode, string][] }) {
  return (
    <div className={items.length > 3 ? "pa-mini four" : "pa-mini"}>
      {items.map(([v, l]) => (
        <div key={l}>
          <b>{v}</b>
          <span>{l}</span>
        </div>
      ))}
    </div>
  );
}

/** A field as a row: its name on the left, the value typed on the right, with an optional $ before or unit after. */
export function FieldRow({
  id,
  label,
  value,
  onChange,
  pre,
  suf,
  inputMode = "decimal",
  type = "text",
  placeholder,
  max,
}: {
  id: string;
  label: ReactNode;
  value: string;
  onChange: (v: string) => void;
  pre?: string;
  suf?: string;
  inputMode?: "decimal" | "numeric" | "text" | "email" | "tel" | "url";
  type?: string;
  placeholder?: string;
  max?: string;
}) {
  const input = (
    <input
      id={id}
      type={type}
      inputMode={inputMode}
      value={value}
      placeholder={placeholder}
      max={max}
      // Why: a $ or a unit sits right against the number, so the box is only as wide as what is typed.
      style={pre || suf ? { width: `calc(${Math.max(value.length, 1)}ch + 2px)` } : undefined}
      onChange={(e) => onChange(e.target.value)}
    />
  );
  return (
    <div className="pn-irow pa-f">
      <label htmlFor={id}>{label}</label>
      {pre || suf ? (
        <span className="pa-val">
          {pre ? <span className="pa-pre">{pre}</span> : null}
          {input}
          {suf ? <span className="pa-suf">{suf}</span> : null}
        </span>
      ) : (
        input
      )}
    </div>
  );
}

/**
 * The app's toast for a native screen, drawn over the whole app. Why: a note said while a sheet is up
 * (a link copied, a payment recorded) must show above the sheet, not under its shade.
 */
export function SayToast({ toast }: { toast: { msg: string; on: boolean; done: boolean } }) {
  return createPortal(
    <div className="pa-say">
      <Toast message={toast.msg} on={toast.on} done={toast.done} />
    </div>,
    document.body,
  );
}
